import { type AnalyticsEvents, type BaseAnalyticsEvent } from '../types/events'
import { DniAnalytics } from './Analytics'

export interface MetaPixelConfig {
  measurementId?: string
  userId?: string | null
}

export class MetaPixel extends DniAnalytics<AnalyticsEvents> {
  readonly config: MetaPixelConfig
  private static instance: MetaPixel | null = null

  constructor(config: MetaPixelConfig) {
    super()
    this.config = config
    this.initialize()
  }

  // Static getInstance method for singleton pattern
  public static getInstance(config: MetaPixelConfig): MetaPixel {
    if (!MetaPixel.instance) {
      MetaPixel.instance = new MetaPixel(config)
    }
    return MetaPixel.instance
  }

  /**
   * External ID 是顧客的識別碼，詳細資訊請參考
   * https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters
   */
  protected initialize(): void {
    if (!this.config.measurementId) {
      console.error('[META_PIXEL] measurementId is not defined')
      return
    }
    this.setIsReady(false)
    const head = document.querySelector('head')
    const existingScript = document.querySelector('script[src*="connect.facebook.net/en_US/fbevents.js"]')
    // https://www.facebook.com/help/1604282442927573
    // Converted textContent is in sc-44553
    // Confirm only init once
    if (!existingScript) {
      const initEvent: unknown[] = ['init', this.config.measurementId]
      if (this.config.userId) {
        initEvent.push({ external_id: this.config.userId })
      }
      const script = document.createElement('script')
      script.textContent = `
      !function(f,b,e,v,n,t,s)
      {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window,document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
      fbq(...${JSON.stringify(initEvent)});
      fbq('track', 'PageView');
      `
      head?.appendChild(script)
      /**
       * Meta Pixel 初始化後本身有 queue 所以可以放心設定成 ready
       * 主要是下面這段
       * f.fbq = function () {
       *  n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
       * };
       */
      this.setIsReady(true)
    }
    else {
      this.setIsReady(true)
    }
  }

  protected sendAnalyticsEvent(customEvent: BaseAnalyticsEvent) {
    if (!window.fbq) {
      console.error('[META_PIXEL] window.fbq is not defined')
      return
    }
    try {
      const { eventName, eventParams, eventId } = customEvent
      const fbqEvent = ['track', eventName, eventParams]
      if (eventId) {
        fbqEvent.push({ eventID: eventId })
      }
      window.fbq(...fbqEvent)
    }
    catch (error) {
      console.error('[META] sendEvent', error)
    }
  }
}
